import { OfficeInstructions } from "@/components/ScheduleInstructions";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { GetDirectionsLink } from "@/components/MapPreview";
import { LogTaskHoursDialog } from "@/components/TaskHourLog";
import WeatherChip from "@/components/WeatherChip";
import { Truck, Clock, MapPin, FileCheck2, Users as UsersIcon, Lock, Phone, ListChecks, Check, ChevronRight } from "lucide-react";
import { SsspSignPrompt } from "@/components/SsspSignPrompt";
import { toast } from "sonner";

export function TodaysRunSheet() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    api.get("/runsheet/today")
      .then((r) => {setData(r.data);setOffline(!!r.offline);})
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load run-sheet"))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return (
      <div className="hard-card p-5 mb-4 animate-pulse" data-testid="runsheet-loading">
        <div className="h-3 w-32 bg-zinc-200" />
        <div className="h-6 w-60 bg-zinc-200 mt-3" />
        <div className="h-20 w-full bg-zinc-100 mt-4" />
      </div>
    );
  }

  if (error) return null;

  const todayLabel = data?.for_date
    ? new Date(data.for_date).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short" })
    : "Today";

  const carried = data?.assigned_tasks?.length ? <AssignedTasks key={data.assigned_tasks.map((t) => t.id).join()} tasks={data.assigned_tasks} /> : null;

  if (data.no_work) {
    return (
      <div className="rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 p-5 mb-4" data-testid="runsheet-no-work">
        <div className="label-tag mb-1">Today · {todayLabel}</div>
        <h3 className="font-display font-bold uppercase text-xl leading-tight text-zinc-900 dark:text-white">Day off</h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">No work scheduled for you today. Enjoy the break.</p>
        {carried}
      </div>
    );
  }

  if (!data.jobs?.length) {
    return (
      <div className="rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 p-5 mb-4" data-testid="runsheet-empty">
        <div className="label-tag mb-1">Today · {todayLabel}</div>
        <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1">No jobs assigned for today yet. Check back later or ping your supervisor.</p>
        {carried}
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="runsheet">
      {offline&&<p className="text-xs text-amber-700 dark:text-amber-300 mb-2">Offline · last saved schedule and instructions. Changes may not be reflected.</p>}
      {/* Header row */}
      <div className="flex items-end justify-between mb-3 px-1">
        <div>
          <div className="label-tag !text-[#FF5F15]">Today · {todayLabel}</div>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl leading-none mt-1 text-zinc-900 dark:text-white">
            Run-sheet
          </h2>
        </div>
        <div className="px-3 py-1.5 rounded-xl bg-zinc-100 dark:bg-[#101620] border border-zinc-200/70 dark:border-white/10 text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-300">
          {data.jobs.length} {data.jobs.length === 1 ? "job" : "jobs"}
        </div>
      </div>

      {/* Vehicle */}
      {data.vehicle && <VehicleStrip vehicle={data.vehicle} />}
      {carried && <div className="rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 px-4 pb-3 mt-3">{carried}</div>}

      {/* Jobs */}
      <div className="space-y-3 mt-3">
        {data.jobs.map((j) => (
          <RunSheetJob key={j.id} job={j} />
        ))}
      </div>
    </div>
  );
}

function VehicleStrip({ vehicle }) {
  return (
    <Link
      to="/app/prestarts"
      className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900 dark:bg-[#101620] border border-transparent dark:border-white/10 text-white hover:bg-zinc-800 dark:hover:bg-[#151C27] transition-colors"
      data-testid="runsheet-vehicle"
    >
      {vehicle.photo_path ? (
        <div className="w-16 h-16 shrink-0 overflow-hidden rounded-xl bg-zinc-700">
          <AuthImage path={vehicle.photo_path} alt={vehicle.rego} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-16 h-16 shrink-0 flex items-center justify-center rounded-xl bg-zinc-800">
          <Truck className="w-7 h-7 text-zinc-500" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Your vehicle</div>
        <div className="font-display font-bold uppercase tracking-tight text-lg">{vehicle.rego}</div>
        <div className="text-xs text-zinc-400 truncate">{vehicle.make} {vehicle.model}</div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] shrink-0">
        Pre-start →
      </div>
    </Link>
  );
}

function RunSheetWeather({ lat, lng, jobId }) {
  const [day, setDay] = useState(null);
  useEffect(() => {
    let m = true;
    if (!lat || !lng) return;
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    api.get("/weather/forecast", { params: { lat, lng } })
      .then((r) => m && setDay((r.data?.days || []).find((d) => d.date === iso) || null))
      .catch(() => m && setDay(null));
    return () => { m = false; };
  }, [lat, lng]);
  if (!lat || !lng || !day) return null;
  return (
    <div className="mt-2 flex items-center gap-2" data-testid={`runsheet-weather-${jobId}`}>
      <WeatherChip day={day} compact />
      <span className="text-[10px] font-mono text-zinc-500">
        {Math.round(day.temp_max ?? 0)}° · {(day.rain_mm || 0).toFixed(1)}mm · wind {(day.wind_kmh || 0).toFixed(0)}km/h
      </span>
    </div>
  );
}

function RunSheetJob({ job }) {
  const start = job.dispatch_start_time || job.start_time || "Time unspecified";
  const sssp = job.sssp;
  return (
    <div className="rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 p-4" data-testid={`runsheet-job-${job.id}`}>
      {/* Top row: title + start time */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex items-start gap-2">
          <MapPin className="w-4 h-4 text-[#FF5F15] mt-1 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-display font-bold uppercase text-base leading-snug text-zinc-900 dark:text-white">{job.title}</h3>
            {job.client_name && (
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400 font-mono mt-0.5 truncate">{job.client_name}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white dark:bg-[#151C27] border border-zinc-200 dark:border-white/10 text-zinc-900 dark:text-white shrink-0">
          <Clock className="w-3 h-3 text-[#FF5F15]" />
          <span className="font-mono text-xs font-bold">{start}</span>
        </div>
      </div>

      {/* Address row with Directions */}
      {job.site_address && (
        <div className="mt-3 flex items-start gap-2 text-sm">
          <MapPin className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
          <span className="text-zinc-700 dark:text-zinc-300 truncate flex-1">{job.site_address}</span>
          <GetDirectionsLink lat={job.site_lat} lng={job.site_lng} address={job.site_address} testid={`runsheet-directions-${job.id}`} />
        </div>
      )}

      {/* Compact site weather — so the crew knows what conditions to expect */}
      <RunSheetWeather lat={job.site_lat} lng={job.site_lng} jobId={job.id} />

      <OfficeInstructions crew={job.crew_instruction} personal={job.scope_of_works}/>

      {/* Crew mates — who else is supposed to be on this job today */}
      {job.crew_mates?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 whitespace-nowrap">
              <UsersIcon className="w-3 h-3" />
              On this job with you ({job.crew_mates.length})
            </div>
            <Link
              to="/app/chat"
              className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] hover:text-[#E65100] whitespace-nowrap"
              data-testid={`runsheet-mate-noshow-${job.id}`}
            >
              No-show? Ping office →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {job.crew_mates.map((m) => (
              <CrewMateChip key={m.id} mate={m} testid={`runsheet-mate-${job.id}-${m.id}`} />
            ))}
          </div>
        </div>
      )}

      {/* Today's tasks — office picks them on the dispatch board */}
      <Link to={`/app/jobs/${job.id}/safety#site-audit`} className="flex items-center justify-center min-h-12 rounded-lg border border-orange-200 text-[#c7440a] font-semibold mt-4">Site Audit Pass</Link>
      <RunSheetTasks job={job} />

      {/* SSSPs the worker still needs to sign (uploaded / own SSSPs) */}
      <SsspSignPrompt items={job.unsigned_sssps} testidPrefix={`runsheet-${job.id}`} />

      {/* SSSP link */}
      <div className="mt-3 flex items-center gap-2 pt-3 border-t border-zinc-200 dark:border-white/10">
        {sssp ? (
          <Link
            to={`/app/sssps/${sssp.id}`}
            className="flex-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-900 dark:text-white hover:text-[#FF5F15] dark:hover:text-[#FF5F15] transition-colors"
            data-testid={`runsheet-sssp-${job.id}`}
          >
            <FileCheck2 className="w-4 h-4 text-[#FF5F15]" />
            <span className="truncate flex-1">SSSP — {sssp.name}</span>
            {sssp.status === "locked" && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest rounded-md bg-green-100 text-green-800 border border-green-300 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30 px-1.5 py-0.5">
                <Lock className="w-2.5 h-2.5" /> Locked
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
          </Link>
        ) : (
          <Link
            to="/app/sssps"
            className="flex-1 flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-[#FF5F15] transition-colors"
            data-testid={`runsheet-sssp-${job.id}`}
          >
            <FileCheck2 className="w-4 h-4" />
            <span className="italic">No SSSP for this job — add one</span>
          </Link>
        )}
      </div>
    </div>
  );
}


// Open tasks assigned to this worker on jobs they're not on today (carried over).
function AssignedTasks({ tasks: initial }) {
  const [tasks, setTasks] = useState(initial);
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });
  const toggle = async (task) => {
    setTasks((arr) => arr.map((t) => (t.id === task.id ? { ...t, status: t.status === "done" ? "open" : "done" } : t)));
    try {
      const { data } = await api.post(`/job-tasks/${task.id}/toggle`);
      if (!data?.offline_queued) setTasks((arr) => arr.map((t) => (t.id === task.id ? { ...t, ...data } : t)));
    } catch {
      toast.error("Couldn't update the task — try again");
      setTasks((arr) => arr.map((t) => (t.id === task.id ? task : t)));
    }
  };
  return (
    <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10" data-testid="runsheet-assigned-tasks">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 mb-1.5">
        <ListChecks className="w-3 h-3" /> Your outstanding tasks
      </div>
      <div className="space-y-1">
        {tasks.map((t) => (
          <div key={t.id} className={`flex items-start gap-2.5 p-2 rounded-xl border ${t.status === "done" ? "bg-green-50 border-green-200 dark:bg-emerald-500/10 dark:border-emerald-500/30" : "bg-white border-zinc-200 dark:bg-[#151C27] dark:border-white/10"}`} data-testid={`runsheet-assigned-task-${t.id}`}>
            <button type="button" onClick={() => toggle(t)} aria-label={t.status === "done" ? "Mark as not done" : "Mark as done"} data-testid={`runsheet-assigned-task-toggle-${t.id}`}
              className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center ${t.status === "done" ? "bg-green-600 border-green-600 text-white" : "border-zinc-400 dark:border-zinc-500 hover:border-[#FF5F15]"}`}>
              {t.status === "done" && <Check className="w-3.5 h-3.5" />}
            </button>
            <span className="min-w-0 flex-1">
              <span className={`block text-sm font-medium break-words ${t.status === "done" ? "line-through text-zinc-400" : "text-zinc-900 dark:text-white"}`}>{t.title}</span>
              <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                {t.job_title || "Job"} · {t.scheduled_date < today ? <span className="text-amber-700 dark:text-amber-300">Overdue since {new Date(`${t.scheduled_date}T12:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}</span> : "Due today"}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Task checklist on the run-sheet. Shows the tasks the office assigned to
// THIS worker for today (assignment.task_ids) — falls back to the job's
// full open task list when the office didn't pick specific ones. Tap to
// mark done; completion is stamped server-side with who/when.
function RunSheetTasks({ job }) {
  const [tasks, setTasks] = useState(job.tasks || []);
  const [logTask, setLogTask] = useState(null);
  const assigned = new Set(job.assigned_task_ids || []);
  const shown = assigned.size ? tasks.filter((t) => assigned.has(t.id)) : tasks;
  if (!shown.length) return null;
  const done = shown.filter((t) => t.status === "done").length;

  const toggle = async (task) => {
    setTasks((arr) => arr.map((t) => t.id === task.id
      ? { ...t, status: t.status === "done" ? "open" : "done" }
      : t));
    try {
      const { data } = await api.post(`/job-tasks/${task.id}/toggle`);
      if (data?.offline_queued) return; // keep the optimistic flip — syncs later
      setTasks((arr) => arr.map((t) => (t.id === task.id ? { ...t, ...data } : t)));
    } catch {
      toast.error("Couldn't update the task — try again");
      setTasks((arr) => arr.map((t) => (t.id === task.id ? task : t)));
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10" data-testid={`runsheet-tasks-${job.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
          <ListChecks className="w-3 h-3" />
          {assigned.size ? "Your tasks today" : "Job tasks"}
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
          {done}/{shown.length} done
        </span>
      </div>
      <div className="space-y-1">
        {shown.map((t) => {
          const tracked = t.task_type === "hour_tracked";
          return (
            <div
              key={t.id}
              className={`w-full flex items-start gap-2.5 p-2 rounded-xl border transition-colors ${
                t.status === "done"
                  ? "bg-green-50 border-green-200 dark:bg-emerald-500/10 dark:border-emerald-500/30"
                  : "bg-white border-zinc-200 dark:bg-[#151C27] dark:border-white/10"
              }`}
              data-testid={`runsheet-task-${t.id}`}
            >
              <button
                type="button"
                onClick={() => toggle(t)}
                className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${
                  t.status === "done" ? "bg-green-600 border-green-600 text-white" : "border-zinc-400 dark:border-zinc-500 hover:border-[#FF5F15]"
                }`}
                data-testid={`runsheet-task-toggle-${t.id}`}
                aria-label={t.status === "done" ? "Mark as not done" : "Mark as done"}
              >
                {t.status === "done" && <Check className="w-3.5 h-3.5" />}
              </button>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-medium break-words ${t.status === "done" ? "line-through text-zinc-400" : "text-zinc-900 dark:text-white"}`}>
                  {t.title}
                </span>
                {t.description && (
                  <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 break-words">{t.description}</span>
                )}
                {tracked && (
                  <span className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FF5F15]" data-testid={`runsheet-task-logged-${t.id}`}>
                      <Clock className="w-3 h-3" />
                      {(t.logged_hours || 0).toFixed(1)}h logged{t.budget_hours ? ` / ${t.budget_hours}h` : ""}
                    </span>
                    {t.status !== "done" && (
                      <button
                        type="button"
                        onClick={() => setLogTask(t)}
                        className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#FF5F15] hover:bg-[#E65100] px-2 py-0.5 rounded-md transition-colors"
                        data-testid={`runsheet-task-log-hours-${t.id}`}
                      >
                        + Log hrs
                      </button>
                    )}
                  </span>
                )}
                {t.status === "done" && t.completed_by_name && (
                  <span className="block text-[9px] font-mono uppercase tracking-widest text-green-700 dark:text-emerald-400 mt-0.5">
                    ✓ {t.completed_by_name}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {logTask && (
        <LogTaskHoursDialog
          task={logTask}
          onClose={() => setLogTask(null)}
          onLogged={(total) => {
            setTasks((arr) => arr.map((t) => (t.id === logTask.id ? { ...t, logged_hours: total } : t)));
            setLogTask(null);
          }}
        />
      )}
    </div>
  );
}


// Crew-mate chip — shows initials + first name + role, with a tap-to-call
// button when the user has a phone number on their profile. Designed for
// the field worker's run-sheet so they can ring a teammate who hasn't shown
// up before bothering the office.
function CrewMateChip({ mate, testid }) {
  const first = (mate.name || "").split(" ")[0] || mate.name || "";
  const initials = (mate.name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const phone = (mate.phone || "").trim();
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, "")}` : null;
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-[#151C27] px-3 py-2 text-[11px] min-w-0"
      data-testid={testid}
    >
      <span className="w-9 h-9 shrink-0 rounded-full bg-[#FF5F15]/15 text-[#FF5F15] flex items-center justify-center text-[11px] font-bold">
        {initials}
      </span>
      <div className="leading-tight min-w-0 flex-1">
        <div className="font-bold text-[13px] text-zinc-900 dark:text-white truncate">{first}</div>
        {mate.role && (
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400 truncate">
            {mate.role}
          </div>
        )}
      </div>
      {telHref ? (
        <a
          href={telHref}
          className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#FF5F15] hover:bg-[#FF5F15]/10 transition-colors"
          aria-label={`Call ${first}`}
          data-testid={`${testid}-call`}
        >
          <Phone className="w-4 h-4" />
        </a>
      ) : (
        <span
          className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-zinc-300 dark:text-zinc-600"
          title="No phone on file"
        >
          <Phone className="w-4 h-4" />
        </span>
      )}
    </div>
  );
}
